import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin } from 'rxjs';

import { Cliente, Posto } from '@core/models/models';
import { ClienteService, PostoService } from '@core/services/domain-services';
import { FeedbackService } from '@core/services/feedback.service';
import {
  CriarDocumentoRequestDto,
  DocumentLineRequestDto,
  FeDocumento,
  FeProduto,
  FeSerie,
  FeTaxCode,
  FeTaxExemptionCode,
  FeTipoDocumento,
} from '@shared/api/models/faturacao-eletronica.model';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';

@Component({
  selector: 'app-fatura-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
  ],
  templateUrl: './fatura-wizard.component.html',
  styleUrl: './fatura-wizard.component.scss',
})
export class FaturaWizardComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly faturacaoService = inject(FaturacaoEletronicaService);
  private readonly clienteService = inject(ClienteService);
  private readonly postoService = inject(PostoService);
  private readonly feedback = inject(FeedbackService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Estado do wizard (Passos 1 a 4)
  currentStep = signal<number>(1);
  loading = signal<boolean>(true);
  saving = signal<boolean>(false);
  submittingAgt = signal<boolean>(false);

  // Modo de operação (novo, editar, corrigir rejeitada)
  editId: number | null = null;
  corrigirId: number | null = null;
  documentoOriginal: FeDocumento | null = null;

  // Listas auxiliares reais vindas da API
  tiposDocumento: FeTipoDocumento[] = [];
  series: FeSerie[] = [];
  clientes: Cliente[] = [];
  postosDoCliente: Posto[] = [];
  produtos: FeProduto[] = [];
  taxCodes: FeTaxCode[] = [];
  isencoes: FeTaxExemptionCode[] = [];

  // Formulário reativo principal
  form!: FormGroup;

  // Cliente selecionado em memória para renderização no Passo 2 e 4
  clienteSelecionado: Cliente | null = null;
  postoSelecionado: Posto | null = null;

  // Coordenadas bancárias oficiais SecurPro para liquidação
  readonly contasBancarias = [
    { banco: 'Banco BAI', titular: 'SecurPro Angola Segurança, Lda', iban: 'AO06.0040.0000.4773.8518.1018.8', conta: '47738518' },
    { banco: 'Banco BIC', titular: 'SecurPro Angola Segurança, Lda', iban: 'AO06.0051.0000.2170.0811.1017.9', conta: '221700811' },
    { banco: 'Banco SOL', titular: 'SecurPro Angola Segurança, Lda', iban: 'AO06.0044.0000.0014.5424.1011.6', conta: '145424' },
    { banco: 'Banco BCA', titular: 'SecurPro Angola Segurança, Lda', iban: 'AO06.0043.0000.0282.2119.1011.1', conta: '002822119' },
  ];

  ngOnInit(): void {
    this.initForm();

    // Detetar parâmetros de rota (edição ou correção)
    const idParam = this.route.snapshot.paramMap.get('id');
    const isCorrigir = this.route.snapshot.url.some((u) => u.path === 'corrigir');

    if (idParam) {
      const parsedId = Number(idParam);
      if (isCorrigir) {
        this.corrigirId = parsedId;
      } else {
        this.editId = parsedId;
      }
    }

    this.carregarDadosIniciais();
  }

  private initForm(): void {
    const hoje = new Date();
    const vencimento5Dias = new Date();
    vencimento5Dias.setDate(vencimento5Dias.getDate() + 5);

    this.form = this.fb.group({
      // Passo 1: Tipo & Série
      idTipoDocumento: [null, [Validators.required]],
      seriesCode: [''],
      dataEmissao: [hoje, [Validators.required]],
      dataVencimento: [vencimento5Dias, [Validators.required]],
      condicaoPagamento: ['5 dias'],
      regimeFiscal: ['Regime Geral (IVA 14%)'],
      moeda: ['AOA'],
      descontoGeral: [0, [Validators.min(0), Validators.max(100)]],
      seriesContingencyIndicator: [false],

      // Passo 2: Cliente & Posto
      idCliente: [null, [Validators.required]],
      idPosto: [null],

      // Passo 3: Itens & Linhas
      lines: this.fb.array([]),
    });

    // Se o cliente mudar, recarregar postos
    this.form.get('idCliente')?.valueChanges.subscribe((clienteId: number) => {
      this.onClienteChange(clienteId);
    });

    // Se o posto mudar, atualizar posto selecionado
    this.form.get('idPosto')?.valueChanges.subscribe((postoId: number) => {
      this.postoSelecionado = this.postosDoCliente.find((p) => p.id === postoId) || null;
    });
  }

  get lines(): FormArray {
    return this.form.get('lines') as FormArray;
  }

  addLine(initial?: Partial<DocumentLineRequestDto>): void {
    const defaultTax = this.taxCodes.find((t) => t.taxCode === 'NOR') || this.taxCodes[0];

    const lineGroup = this.fb.group({
      idProduct: [initial?.idProduct ?? null, [Validators.required]],
      quantity: [initial?.quantity ?? 1, [Validators.required, Validators.min(0.001)]],
      unitPrice: [initial?.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
      idTaxCode: [initial?.idTaxCode ?? defaultTax?.id ?? null, [Validators.required]],
      precoComImpostoIncluido: [initial?.precoComImpostoIncluido ?? false],
      idTaxExemptionCode: [initial?.idTaxExemptionCode ?? null],
    });

    this.lines.push(lineGroup);
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) {
      this.lines.removeAt(index);
    } else {
      this.feedback.showWarning('A fatura deve possuir pelo menos uma linha de serviço ou produto.');
    }
  }

  private carregarDadosIniciais(): void {
    this.loading.set(true);

    forkJoin({
      tipos: this.faturacaoService.listarTiposDocumento(),
      series: this.faturacaoService.listarSeries(),
      clientes: this.clienteService.getAll({ pageSize: 100 }),
      produtos: this.faturacaoService.listarProdutos(),
      taxCodes: this.faturacaoService.listarTaxCodes(),
      isencoes: this.faturacaoService.listarIsencoes(),
    }).subscribe({
      next: (res) => {
        this.tiposDocumento = res.tipos;
        this.series = res.series;
        this.clientes = res.clientes.data || [];
        this.produtos = res.produtos;
        this.taxCodes = res.taxCodes;
        this.isencoes = res.isencoes;

        // Auto-selecionar tipo FT (Fatura) se existir
        const tipoFt = this.tiposDocumento.find((t) => t.codigo === 'FT') || this.tiposDocumento[0];
        if (tipoFt && !this.form.get('idTipoDocumento')?.value) {
          this.form.get('idTipoDocumento')?.setValue(tipoFt.id);
        }

        // Auto-selecionar série ativa correspondente
        if (this.series.length > 0 && !this.form.get('seriesCode')?.value) {
          this.form.get('seriesCode')?.setValue(this.series[0].seriesCode);
        }

        // Se estiver em modo de edição ou correção, carregar documento original
        if (this.editId || this.corrigirId) {
          const targetId = this.editId || this.corrigirId;
          this.carregarDocumentoParaEdicao(targetId!);
        } else {
          // Adicionar primeira linha vazia
          if (this.lines.length === 0) {
            this.addLine();
          }
          this.loading.set(false);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.feedback.showError(err, 'Erro ao carregar dados auxiliares de faturação.');
      },
    });
  }

  private carregarDocumentoParaEdicao(id: number): void {
    this.faturacaoService.obterDocumento(id).subscribe({
      next: (doc) => {
        this.documentoOriginal = doc;

        this.form.patchValue({
          idTipoDocumento: doc.idTipoDocumento,
          idCliente: doc.idCliente,
          seriesContingencyIndicator: !!doc.seriesContingencyIndicator,
        });

        if (doc.lines && doc.lines.length > 0) {
          this.lines.clear();
          for (const l of doc.lines) {
            const taxId = l.taxes && l.taxes.length > 0
              ? (this.taxCodes.find((t) => t.taxCode === l.taxes![0].taxCode)?.id ?? null)
              : null;

            this.addLine({
              idProduct: l.idProduct,
              quantity: l.quantity,
              unitPrice: l.unitPrice,
              idTaxCode: taxId ?? this.taxCodes[0]?.id,
              precoComImpostoIncluido: false,
            });
          }
        } else if (this.lines.length === 0) {
          this.addLine();
        }

        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.feedback.showError(err, 'Não foi possível carregar os dados do documento.');
      },
    });
  }

  private onClienteChange(clienteId: number): void {
    this.clienteSelecionado = this.clientes.find((c) => c.id === clienteId) || null;
    this.postoSelecionado = null;
    this.postosDoCliente = [];
    this.form.get('idPosto')?.setValue(null);

    if (clienteId) {
      this.postoService.getAll({ idCliente: clienteId }).subscribe({
        next: (res) => {
          this.postosDoCliente = res.data || [];
          if (this.postosDoCliente.length === 1 && this.postosDoCliente[0].id) {
            this.form.get('idPosto')?.setValue(this.postosDoCliente[0].id);
          }
        },
        error: () => {
          this.postosDoCliente = [];
        },
      });
    }
  }

  onProductChange(lineIndex: number, productId: number): void {
    const prod = this.produtos.find((p) => p.id === productId);
    if (prod) {
      const lineGroup = this.lines.at(lineIndex) as FormGroup;
      if (prod.unitPrice || prod.preco) {
        lineGroup.patchValue({ unitPrice: prod.unitPrice || prod.preco });
      }
    }
  }

  setCondicaoPagamento(dias: number, label: string): void {
    this.form.get('condicaoPagamento')?.setValue(label);
    const emissao = this.form.get('dataEmissao')?.value as Date || new Date();
    const vencimento = new Date(emissao);
    vencimento.setDate(vencimento.getDate() + dias);
    this.form.get('dataVencimento')?.setValue(vencimento);
  }

  requiresExemption(lineIndex: number): boolean {
    const lineGroup = this.lines.at(lineIndex) as FormGroup;
    const taxId = lineGroup.get('idTaxCode')?.value;
    const tax = this.taxCodes.find((t) => t.id === taxId);
    return !!tax && tax.taxPercentage === 0;
  }

  availableExemptions(lineIndex: number): FeTaxExemptionCode[] {
    const lineGroup = this.lines.at(lineIndex) as FormGroup;
    const taxId = lineGroup.get('idTaxCode')?.value;
    return this.isencoes.filter((e) => !e.idTaxCode || e.idTaxCode === taxId);
  }

  // Cálculos financeiros em tempo real
  get subtotalNetEstimado(): number {
    let total = 0;
    for (const ctrl of this.lines.controls) {
      const q = Number(ctrl.get('quantity')?.value) || 0;
      const p = Number(ctrl.get('unitPrice')?.value) || 0;
      const comImposto = !!ctrl.get('precoComImpostoIncluido')?.value;
      const taxId = ctrl.get('idTaxCode')?.value;
      const tax = this.taxCodes.find((t) => t.id === taxId);
      const taxPercent = tax?.taxPercentage || 0;

      if (comImposto && taxPercent > 0) {
        const netUnit = p / (1 + taxPercent / 100);
        total += q * netUnit;
      } else {
        total += q * p;
      }
    }
    return Math.round(total * 100) / 100;
  }

  get totalIvaEstimado(): number {
    let total = 0;
    for (const ctrl of this.lines.controls) {
      const q = Number(ctrl.get('quantity')?.value) || 0;
      const p = Number(ctrl.get('unitPrice')?.value) || 0;
      const comImposto = !!ctrl.get('precoComImpostoIncluido')?.value;
      const taxId = ctrl.get('idTaxCode')?.value;
      const tax = this.taxCodes.find((t) => t.id === taxId);
      const taxPercent = tax?.taxPercentage || 0;

      if (comImposto && taxPercent > 0) {
        const netUnit = p / (1 + taxPercent / 100);
        total += q * (p - netUnit);
      } else {
        total += q * p * (taxPercent / 100);
      }
    }
    return Math.round(total * 100) / 100;
  }

  get retencaoEstimada(): number {
    // Retenção na fonte Lei 19/14 (6,5% sobre serviços de pessoas coletivas)
    return Math.round(this.subtotalNetEstimado * 0.065 * 100) / 100;
  }

  get totalBrutoEstimado(): number {
    return Math.round((this.subtotalNetEstimado + this.totalIvaEstimado) * 100) / 100;
  }

  get numeroOficialPrevisto(): string {
    const serieCode = this.form.get('seriesCode')?.value || 'SERIE2026';
    const tipo = this.tiposDocumento.find((t) => t.id === this.form.get('idTipoDocumento')?.value)?.codigo || 'FT';
    return `${tipo} ${serieCode}/... (atribuído ao guardar)`;
  }

  // Navegação do Wizard
  goToStep(step: number): void {
    if (step < 1 || step > 4) return;

    if (step > this.currentStep()) {
      if (!this.validarPassoAtual()) {
        return;
      }
    }
    this.currentStep.set(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  validarPassoAtual(): boolean {
    const step = this.currentStep();
    if (step === 1) {
      const idTipo = this.form.get('idTipoDocumento');
      const dataEmissao = this.form.get('dataEmissao');
      const dataVenc = this.form.get('dataVencimento');

      idTipo?.markAsTouched();
      dataEmissao?.markAsTouched();
      dataVenc?.markAsTouched();

      if (idTipo?.invalid || dataEmissao?.invalid || dataVenc?.invalid) {
        this.feedback.showWarning('Preencha os campos obrigatórios do Tipo de Documento e Datas.');
        return false;
      }
      return true;
    }

    if (step === 2) {
      const idCliente = this.form.get('idCliente');
      idCliente?.markAsTouched();
      if (idCliente?.invalid) {
        this.feedback.showWarning('Selecione um cliente para o documento.');
        return false;
      }
      return true;
    }

    if (step === 3) {
      if (this.lines.length === 0) {
        this.feedback.showWarning('Adicione pelo menos um serviço ou produto.');
        return false;
      }
      this.lines.markAllAsTouched();
      if (this.lines.invalid) {
        this.feedback.showWarning('Verifique as linhas de serviços/produtos: produto, quantidade, preço e imposto são obrigatórios.');
        return false;
      }
      return true;
    }

    return true;
  }

  // Montar payload real conforme CriarDocumentoRequestDto do Swagger
  private buildPayload(): CriarDocumentoRequestDto {
    const raw = this.form.getRawValue();

    const linesDto: DocumentLineRequestDto[] = raw.lines.map((l: any) => ({
      idProduct: Number(l.idProduct),
      quantity: Number(l.quantity),
      unitPrice: Number(l.unitPrice),
      idTaxCode: Number(l.idTaxCode),
      precoComImpostoIncluido: Boolean(l.precoComImpostoIncluido),
      idTaxExemptionCode: l.idTaxExemptionCode ? Number(l.idTaxExemptionCode) : null,
    }));

    return {
      idTipoDocumento: Number(raw.idTipoDocumento),
      idCliente: Number(raw.idCliente),
      seriesContingencyIndicator: Boolean(raw.seriesContingencyIndicator),
      lines: linesDto,
    };
  }

  // Guardar Rascunho (cria documento no estado 'Guardada')
  guardarRascunho(): void {
    if (!this.validarPassoAtual()) {
      return;
    }

    this.saving.set(true);
    const payload = this.buildPayload();

    if (this.corrigirId) {
      // Correção de fatura rejeitada
      this.faturacaoService.corrigirDocumento(this.corrigirId, payload).subscribe({
        next: (docCriado) => {
          this.saving.set(false);
          this.feedback.showSuccess(
            docCriado,
            `Documento corretivo ${docCriado.documentNo || ''} criado com sucesso no estado Guardada.`,
          );
          this.router.navigate(['/admin/financeiro', docCriado.id]);
        },
        error: (err) => {
          this.saving.set(false);
          this.feedback.showError(err, 'Erro ao criar documento de correção.');
        },
      });
      return;
    }

    if (this.editId) {
      // Atualização de documento existente
      this.faturacaoService.atualizarDocumento(this.editId, payload).subscribe({
        next: (docAtualizado) => {
          this.saving.set(false);
          this.feedback.showSuccess(docAtualizado, 'Documento atualizado com sucesso.');
          this.router.navigate(['/admin/financeiro', docAtualizado.id]);
        },
        error: (err) => {
          this.saving.set(false);
          this.feedback.showError(err, 'Erro ao atualizar documento.');
        },
      });
      return;
    }

    // Criação normal
    this.faturacaoService.criarDocumento(payload).subscribe({
      next: (docCriado) => {
        this.saving.set(false);
        this.feedback.showSuccess(
          docCriado,
          `Fatura ${docCriado.documentNo || ''} guardada com sucesso no estado Guardada.`,
        );
        this.router.navigate(['/admin/financeiro', docCriado.id]);
      },
      error: (err) => {
        this.saving.set(false);
        this.feedback.showError(err, 'Erro ao guardar documento.');
      },
    });
  }

  // Guardar & Submeter à AGT (chama criar/atualizar e em seguida /submeter)
  guardarESubmeter(): void {
    if (!this.validarPassoAtual()) {
      return;
    }

    this.submittingAgt.set(true);
    const payload = this.buildPayload();

    const saveObs = this.editId
      ? this.faturacaoService.atualizarDocumento(this.editId, payload)
      : this.faturacaoService.criarDocumento(payload);

    saveObs.subscribe({
      next: (docCriado) => {
        const id = docCriado.id || this.editId;
        // Submeter à AGT
        this.faturacaoService.submeterDocumento(id!).subscribe({
          next: (docSubmetido) => {
            this.submittingAgt.set(false);
            this.feedback.showSuccess(
              docSubmetido,
              `Fatura ${docSubmetido.documentNo || docCriado.documentNo || ''} submetida à AGT com sucesso (Estado: Submetida).`,
            );
            this.router.navigate(['/admin/financeiro', id]);
          },
          error: (errSubmeter) => {
            this.submittingAgt.set(false);
            this.feedback.showError(
              errSubmeter,
              `Fatura guardada com sucesso (${docCriado.documentNo}), mas a submissão à AGT falhou. Pode tentar submeter a partir da tela de detalhes.`,
            );
            this.router.navigate(['/admin/financeiro', id]);
          },
        });
      },
      error: (errSave) => {
        this.submittingAgt.set(false);
        this.feedback.showError(errSave, 'Erro ao guardar a fatura.');
      },
    });
  }

  cancelar(): void {
    this.router.navigate(['/admin/financeiro']);
  }

  formatCurrency(value?: number | null): string {
    if (value === null || value === undefined) return '0,00 Kz';
    return (
      new Intl.NumberFormat('pt-AO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' Kz'
    );
  }

  getProductName(idProduct: number): string {
    const p = this.produtos.find((x) => x.id === idProduct);
    return p ? p.productDescription : 'Serviço';
  }

  getProductCode(idProduct: number): string {
    const p = this.produtos.find((x) => x.id === idProduct);
    return p ? p.productCode : '—';
  }

  getProductUnit(idProduct: number): string {
    const p = this.produtos.find((x) => x.id === idProduct);
    return p?.unitOfMeasure ? p.unitOfMeasure : 'UN';
  }

  getTaxLabel(idTaxCode: number): string {
    const t = this.taxCodes.find((x) => x.id === idTaxCode);
    return t ? `${t.taxCode} (${t.taxPercentage}%)` : 'IVA';
  }
}
